/**
 * Wie sich zwei übereinanderliegende Bereiche den Platz teilen.
 *
 * Gerechnet wird in Anteilen, nicht in Pixeln: Der Datenraum steht in einer Spalte, deren Höhe vom
 * Fenster abhängt. Ein in Pixeln gemerkter Trennerstand wäre auf einem kleineren Bildschirm falsch
 * — entweder bliebe für den Baum nichts übrig, oder die Angaben verschwänden ganz.
 *
 * Der Anteil ist immer der des **unteren** Bereichs. Nach unten zu ziehen macht ihn kleiner, nach
 * oben größer; das ist die Richtung, in der der Zeiger sich bewegt.
 */

/** Weniger als das bleibt nicht stehen — ein Bereich, der nur noch ein Rand ist, ist kein Bereich. */
export const MIN_SHARE = 0.15;
export const MAX_SHARE = 0.85;

/** Die voreingestellte Aufteilung: der Baum bekommt die Mehrheit, die Angaben gut ein Drittel. */
export const DEFAULT_SHARE = 0.38;

export const clampShare = (share: number, min: number = MIN_SHARE, max: number = MAX_SHARE): number => {
    if (!Number.isFinite(share)) return min;

    return Math.min(max, Math.max(min, share));
};

/**
 * Der Anteil des **hinteren** Bereichs entlang einer Achse.
 *
 * Hinten heißt unten, wenn die Bereiche übereinanderliegen, und rechts, wenn sie nebeneinander
 * stehen. Die Rechnung ist dieselbe; nur die Achse wechselt. Deshalb eine Funktion und nicht zwei:
 * Zwei liefen früher oder später auseinander, und der Trenner soll sich in beiden Richtungen gleich
 * anfühlen.
 *
 * `null` bei einem Behälter ohne Ausdehnung — beim ersten Zeichnen oder in einer eingeklappten
 * Spalte ist er null groß. Dann bleibt der bisherige Stand stehen; auf den Mindestanteil zu
 * springen hieße, dass ein Trenner sich von selbst verstellt, ohne dass jemand ihn angefasst hat.
 */
export const shareFromPointerAlong = (
    pointer: number,
    bounds: { start: number; extent: number },
    limits?: { min?: number; max?: number },
): number | null => {
    if (!Number.isFinite(bounds.extent) || bounds.extent <= 0) return null;

    const fromFarEdge = bounds.start + bounds.extent - pointer;

    return clampShare(fromFarEdge / bounds.extent, limits?.min, limits?.max);
};

/**
 * Der Anteil des unteren Bereichs, wenn der Zeiger bei `clientY` steht.
 *
 * Die senkrechte Fassung von `shareFromPointerAlong`, in der Form, die die übereinanderliegende
 * Aufteilung schon benutzt.
 */
export const shareFromPointer = (
    clientY: number,
    bounds: { top: number; height: number },
    limits?: { min?: number; max?: number },
): number | null => shareFromPointerAlong(clientY, { start: bounds.top, extent: bounds.height }, limits);

/**
 * Ein gemerkter Stand, wieder eingelesen.
 *
 * Was im Speicher des Browsers steht, kommt von irgendwoher — aus einer älteren Fassung, aus einer
 * anderen Ansicht, von Hand geändert. Alles, was keine brauchbare Zahl ergibt, fällt auf die
 * Voreinstellung zurück, statt die Aufteilung zu verstellen.
 */
export const parseShare = (raw: unknown, fallback: number = DEFAULT_SHARE): number => {
    if (raw === null || raw === undefined || raw === '') return fallback;

    const value = Number(raw);

    return Number.isFinite(value) ? clampShare(value) : fallback;
};
